import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { Subscription, Subject, debounceTime } from "rxjs";
import { FilterModelDto } from "src/app/models/filter.model";
import { SortDataColumn, CheckBoxTable } from "src/app/models/table.model";
import { CheckboxTableService } from "src/app/services/checkbox-table.service";
import { ModalService } from "src/app/services/modal.service";
import { NotificationService } from "src/app/services/notification.service";
import { PermissionService } from "src/app/services/permission.service";
import { SortTableService } from "src/app/services/sort-table.service";
import { TableHeightService } from "src/app/services/table-height.service";
import { RoleDto, RoleListSmallDto } from "../role-list/models/role-models";
import { RoleDataService } from "../role-list/services/role-data.service";
import { UserInfoDto } from "./models/user-models";
import { UserDataService } from "./services/user-data.service";
import { FilterSettingService } from "src/app/services/filter-setting.service";
import { FileNameSetService } from "src/app/services/file-name-set.service";
import { ContextMenuService } from "src/app/services/context-menu.service";

@Component({
	selector: "app-user-list",
	templateUrl: "./user-list.component.html",
	styleUrls: ["./user-list.component.scss"],
})
export class UserListComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild("tableBlock") tableBlock: ElementRef | undefined;
	@ViewChild("tableBody") tableBody: ElementRef | undefined;
	@ViewChild("userBlock") userBlock: ElementRef | undefined;

	isOpenFilter = false;
	filter2fa: FilterModelDto[] | undefined;
	filterRoles: FilterModelDto[] | undefined;
	filterStatuses: FilterModelDto[] | undefined;
	filterAdditional: FilterModelDto[] | undefined;

	sortColumnData: SortDataColumn | undefined;

	subs: Subscription[] = [];

	delta = 150;

	roleList: RoleListSmallDto[] = [];
	userInfo: Subject<UserInfoDto> = new Subject<UserInfoDto>();

	isOpenModalAddUser = false;
	isOpenModalEditUser = false;
	isOpenModalDeleteUser = false;
	isOpenModalResetSession = false;
	isOpenModalResetPassword = false;
	isOpenModalChangeStatus = false;

	selectedRow: any[] = [];
	groupStatus: string | "blocked" | "unlocked" | "none" = "none";
	userStatus!: boolean;

	userList: UserInfoDto[] = [];

	currentPage = 0;
	lastPage!: boolean;

	filterData: any;
	searchWord: string | undefined = "";

	fileName = "";
	userName = "";
	userId: number | undefined;

	constructor(
		private userDataService: UserDataService,
		private sortTableService: SortTableService,
		private tableHeightService: TableHeightService,
		private renderer: Renderer2,
		private modalService: ModalService,
		private roleDataService: RoleDataService,
		private checkboxTableService: CheckboxTableService,
		private notificationService: NotificationService,
		public permissionService: PermissionService,
		private filterSettingService: FilterSettingService,
		private fileNameSetService: FileNameSetService,
		public contextMenuService: ContextMenuService
	) {}

	ngOnInit() {
		this.initFilter();
		this.initModal();
		this.getRoleList();
		this.initCheckboxSelectAll();
		this.sortColumn("DATE", "ASC");
		this.fileNameInit();
	}

	ngAfterViewInit() {
		this.subs.push(
			this.tableHeightService.tableHeight$.subscribe({
				next: (res) => {
					this.renderer.setStyle(this.tableBlock?.nativeElement, "height", res);
				},
			})
		);
	}

	ngOnDestroy() {
		this.subs.forEach((item) => item.unsubscribe());
		this.sortTableService.sortColum$.next(undefined);
	}

	initCheckboxSelectAll() {
		this.subs.push(
			this.checkboxTableService.checkboxSelectAll$.subscribe({
				next: (res) => {
					this.selectedRow = [];
					if (res) {
						this.userList.forEach((item) => this.selectedRow.push(item.id));
					}
					this.setStatusGroup();
				},
			})
		);
	}

	getUserList() {
		this.lastPage = true;
		this.userDataService.loadUserList(this.currentPage, this.searchWord, this.filterData).subscribe({
			next: (res) => {
				this.userList.push(...res.content);
				this.currentPage++;
				this.lastPage = res.last;
				this.checkboxTableService.compareCount$.next();
			},
		});
	}

	getRoleList() {
		if (this.permissionService.hasPermission(["USERS_ADD", "USERS_EDIT"])) {
			this.roleDataService.loadRoleListForUserPage().subscribe({
				next: (res) => {
					this.roleList = res;
				},
			});
		}
	}

	initModal() {
		this.subs.push(
			this.modalService.isModalOpen$.subscribe({
				next: (res) => {
					if (!res) {
						this.isOpenModalAddUser = false;
						this.isOpenModalEditUser = false;
						this.isOpenModalDeleteUser = false;
						this.isOpenModalResetSession = false;
						this.isOpenModalChangeStatus = false;
						this.isOpenModalResetPassword = false;
					}
				},
			})
		);
	}

	initFilter() {
		this.subs.push(
			this.userDataService.loadUserFilter().subscribe({
				next: (res) => {
					this.filterRoles = res["roles"];
					this.filter2fa = res["2fa"];
					this.filterStatuses = res["statuses"];
					this.filterAdditional = res["additional_params"];
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterSetData$.subscribe({
				next: (res) => {
					this.filterData = res;
					this.currentPage = 0;

					this.userList = [];
					this.selectedRow = [];
					this.getUserList();
					this.checkboxTableService.compareCount$.next();
				},
			})
		);

		this.subs.push(
			this.filterSettingService.inputSearch$.pipe(debounceTime(300)).subscribe({
				next: (res) => {
					this.userDataService.loadSearchList(res).subscribe({
						next: (res) => {
							this.filterSettingService.responseSearchData$.next(res.content);
						},
					});
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterSearchWord$.subscribe({
				next: (res) => {
					this.searchWord = res;
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterAllReset$.subscribe({
				next: () => {
					this.searchWord = "";
					this.filterData = undefined;
					this.currentPage = 0;

					this.userList = [];
					this.getUserList();
					this.selectedRow = [];
				},
			})
		);
	}

	onScroll(event: any): void {
		const tableBodyHeight = this.tableBody?.nativeElement.clientHeight;
		const tableBlockHeight = this.tableBlock?.nativeElement.clientHeight;
		const scrollTable = event.target.scrollTop;
		if (tableBodyHeight - tableBlockHeight - scrollTable < this.delta && !this.lastPage) {
			this.getUserList();
		}
	}

	@HostListener("document:scroll", ["$event"])
	onWindowScroll(event: any): void {
		if (window.innerWidth <= 768) {
			if (this.userBlock?.nativeElement.clientHeight - window.scrollY - window.innerHeight + 120 < this.delta && !this.lastPage) {
				this.getUserList();
			}
		}
	}

	toggleFilter(filterState: boolean) {
		this.isOpenFilter = filterState;
	}

	fileNameInit() {
		this.subs.push(
			this.fileNameSetService.setFileName$.subscribe({
				next: (res) => {
					this.fileName = res;
				},
			})
		);
	}

	upload() {
		this.userDataService.loadFile().subscribe({
			next: (res) => {
				let link = document.createElement("a");
				link.href = (window.webkitURL || window.URL).createObjectURL(res);
				link.download = this.fileName;
				link.click();
			},
		});
	}

	sortColumn(column: string, order: "ASC" | "DESC" | "" = "") {
		this.sortColumnData = this.sortTableService.sort(column);

		const sort = `order=${order || this.sortColumnData?.order}&field=${this.sortColumnData?.field}`;

		if (order) {
			this.sortColumnData!["order"] = order;
		}
		this.sortTableService.sortColum$.next(sort);

		this.currentPage = 0;
		this.userList = [];
		this.getUserList();
	}

	addUser() {
		this.isOpenModalAddUser = true;
	}

	editUser(id: number) {
		this.userDataService.loadUserInfo(id).subscribe({
			next: (res) => {
				this.userInfo.next(res);
				this.isOpenModalEditUser = true;
			},
		});
	}

	checkChange(item: CheckBoxTable) {
		if (item.state) {
			this.setCheckboxState(item);
		} else {
			this.clearCheckboxState(item);
		}
		this.checkboxTableService.compareCount$.next();
		this.setStatusGroup();
	}

	setUserNameAndCount(id?: number) {
		if (this.selectedRow.length > 1) {
			this.userName = "";
		}
		if (this.selectedRow.length === 1) {
			this.setUserName(this.selectedRow[0]);
		}
		this.setStatusGroup();
	}

	setUserName(id: number) {
		this.userList.forEach((item) => {
			if (item.id === id) {
				this.userName = item.full_name;
				this.userStatus = item.is_enabled;
			}
		});
		this.userId = id;
	}

	deleteUsers(id?: number) {
		if (id) {
			this.setUserName(id);
		} else {
			this.setUserNameAndCount();
		}
		this.isOpenModalDeleteUser = true;
	}

	resetSession(id?: number) {
		if (id) {
			this.setUserName(id);
		} else {
			this.setUserNameAndCount();
		}
		this.isOpenModalResetSession = true;
	}

	resetPassword(id?: number) {
		if (id) {
			this.setUserName(id);
		} else {
			this.setUserNameAndCount();
		}
		this.isOpenModalResetPassword = true;
	}

	changeStatus(id?: number) {
		if (id) {
			this.setUserName(id);
		} else {
			this.setUserNameAndCount();
		}
		this.isOpenModalChangeStatus = true;
	}

	setStatusGroup() {
		let blocked = 0;
		let unlocked = 0;
		if (this.selectedRow.length > 0) {
			for (let i = 0; i < this.userList.length; i++) {
				if (this.selectedRow.includes(this.userList[i].id)) {
					if (this.userList[i].is_enabled) {
						unlocked++;
					} else {
						blocked++;
					}
				}
			}
		}

		if ((blocked !== 0 && unlocked !== 0) || (blocked === 0 && unlocked === 0)) {
			this.groupStatus = "none";
		} else {
			if (blocked !== 0 && unlocked === 0) {
				this.groupStatus = "blocked";
			} else {
				this.groupStatus = "unlocked";
			}
		}
	}

	setCheckboxState(item: CheckBoxTable) {
		this.selectedRow.push(item.id);
		this.setStatusGroup();
	}

	clearCheckboxState(item: CheckBoxTable) {
		for (let i = 0; i < this.selectedRow.length; i++) {
			if (item.id === this.selectedRow[i]) {
				this.selectedRow.splice(i, 1);
				return;
			}
		}
	}

	deleteUserList(deleteUserList: string) {
		const deleteUsers = deleteUserList.split(",");

		this.userDataService.deleteUsers(deleteUserList).subscribe({
			next: () => {
				this.modalService.isModalOpen$.next(false);
				this.notificationService.setConfiguration(
					"icon-6",
					"error",
					"USER_LIST.NOTIFICATION_DELETE.DELETE_USER_HEADLINE",
					"USER_LIST.NOTIFICATION_DELETE.DELETE_USER_DESCRIPTION"
				);

				this.notificationService.showNotification();

				this.selectedRow = [];
				deleteUsers.forEach((item) => {
					for (let i = 0; i < this.userList.length; i++) {
						if (item === this.userList[i].id.toString()) {
							this.userList.splice(i, 1);
						}
					}
				});
			},
		});
	}

	// TODO Нужно будет применить более оптимальный метод открытия уведоления т.к. уведомлений много разного типа
	addedUser() {
		this.notificationService.setConfiguration(
			"icon-4",
			"success",
			"USER_LIST.NOTIFICATION.ADDED_USER_HEADLINE",
			"USER_LIST.NOTIFICATION.ADDED_USER_DESCRIPTION"
		);

		this.notificationService.showNotification();
		// TODO Временно надо будет решить как без доп. запроса обрновлять данные...
		this.userList = [];
		this.currentPage = 0;
		this.getUserList();
	}

	editedUser(id: number) {
		this.notificationService.setConfiguration("icon-2", "warning", "USER_LIST.NOTIFICATION_EDIT.TITLE", "USER_LIST.NOTIFICATION_EDIT.DESCRIPTION");
		this.notificationService.showNotification();
		this.selectedRow = [];
		this.userDataService.loadUserInfo(id).subscribe({
			next: (res) => {
				for (let i = 0; i < this.userList.length; i++) {
					if (this.userList[i].id === res.id) {
						this.userList[i] = res;
						return;
					}
				}
			},
		});
	}

	changeStatusUsers(userIds: string) {
		let state = false;

		if (this.groupStatus !== "none") {
			if (this.groupStatus === "blocked") {
				state = true;
			}
			if (this.groupStatus === "unlocked") {
				state = false;
			}
		} else {
			state = !this.userStatus;
		}

		this.userDataService.changeStatusUser(userIds, state).subscribe({
			next: () => {
				this.modalService.isModalOpen$.next(false);
				this.notificationService.setConfiguration(
					state ? "icon-2" : "icon-1",
					"warning",
					`USER_LIST.${state ? "NOTIFICATION_UNLOCK.TITLE" : "NOTIFICATION_BLOCK.TITLE"}`,
					"USER_LIST.NOTIFICATION_BLOCK.DESCRIPTION"
				);
				this.notificationService.showNotification();

				this.groupStatus = "none";
				this.userList = [];
				this.selectedRow = [];
				this.currentPage = 0;
				this.getUserList();
			},
		});
	}

	resetPasswordEmail(id: string) {
		this.userDataService.resetPasswordEmail(id).subscribe({
			next: () => {
				this.notificationService.setConfiguration(
					"icon-2",
					"warning",
					"USER_LIST.NOTIFICATION_RESET.TITLE",
					"USER_LIST.NOTIFICATION_RESET.DESCRIPTION"
				);
				this.notificationService.showNotification();
			},
		});
	}

	resetSessionUsers(id: string) {
		this.userDataService.resetSession(id).subscribe({
			next: () => {
				this.notificationService.setConfiguration(
					"icon-2",
					"warning",
					"USER_LIST.NOTIFICATION_RESET_SESSION.TITLE",
					"USER_LIST.NOTIFICATION_RESET_SESSION.DESCRIPTION"
				);
				this.notificationService.showNotification();
			},
		});
	}
}
